import type { ParseKeys } from 'i18next'
import type { ReactNode } from 'react'
import { I18n } from '../../components/I18n.tsx'
import { Icon } from '../../components/Icon.tsx'
import { RingPreview } from '../../components/RingPreview.tsx'
import type { ThemeMode } from '../../states.ts'
import type { LayerMaskId, LayerViewId } from './layers.tsx'

export const LIGHTNING_ZOOM_BOUNDARY = 9
export const LTZ_DETAILED_MIN_ZOOM = 5.5
export const LRT_ANIMATED_INTERVAL = 6

export const colorscales = {
	temperatures: {
		light: {
			0: '#8C81D0',
			0.08: '#8C81D0',
			0.16: '#B6ADF1',
			0.23: '#CFC4F4',
			0.25: '#FFFFFF',
			0.27: '#AACFFF',
			0.4: '#B7FFDC',
			0.5: '#D4FFC5',
			0.6: '#F8F5B2',
			0.68: '#FFD997',
			0.75: '#FF9D90',
			0.82: '#D56A66',
			1: '#D56A66',
		},
		dark: {
			0: '#24222F',
			0.08: '#24222F',
			0.16: '#332D47',
			0.23: '#352B4C',
			0.25: 'rgba(16,16,16,0.2)',
			0.27: '#1F3654',
			0.4: '#264436',
			0.5: '#263A1F',
			0.6: '#434114',
			0.68: '#4E3914',
			0.75: '#662017',
			0.82: '#490502',
			1: '#490502',
		},
	},
	'radar-rain': {
		0.0123: 'rgba(0,0,0,0)',
		0.0125: 'rgba(140, 244, 130, 0.8)', //'#8CF482',
		0.0625: 'rgba(73, 235, 58, 0.8)', //'#49EB3A',
		0.35: 'rgba(17, 76, 0, 0.8)', //'#114C00',
		0.4375: 'rgba(228, 199, 0, 0.8)', //'#E4C700',
		0.5625: 'rgba(218, 0, 0, 0.8)', //'#DA0000',
		0.6875: 'rgba(122, 0, 9, 0.8)', //'#7A0009',
		0.75: 'rgba(158, 5, 112, 0.8)', //'#9E0570',
		0.8125: 'rgba(220, 66, 177, 0.8)', //'#DC42B1',
		0.9375: 'rgba(222, 168, 255, 0.8)', //'#DEA8FF',
	},
	'radar-snow': {
		0: 'rgba(123, 255, 254, 0.8)', //'#7BFFFE',
		0.17: 'rgba(1, 178, 246, 0.8)', //'#01B2F6',
		0.34: 'rgba(0, 112, 236, 0.8)', //'#0070EC',
		0.66: 'rgba(0, 24, 188, 0.8)', //'#0018BC',
		0.83: 'rgba(28, 0, 196, 0.8)', //'#1C00C4',
		1: 'rgba(92, 1, 244, 0.8)', //'#5C01F4',
	},
	'radar-mix': {
		0: 'rgba(255, 163, 249, 0.8)', //'#FFA3F9',
		0.17: 'rgba(236, 99, 221, 0.8)', //'#EC63DD',
		0.34: 'rgba(216, 37, 195, 0.8)', //'#D825C3',
		0.66: 'rgba(156, 14, 130, 0.8)', //'#9C0E82',
		0.83: 'rgba(121, 6, 95, 0.8)', //'#79065F',
		1: 'rgba(88, 0, 59, 0.8)', //'#58003B',
	},
	snow: {
		light: {
			0: 'rgba(96,96,96,0)',
			0.00003: 'rgba(96,96,96,0)',
			0.0003: 'rgba(96,96,96,0.5)',
			0.003: '#576066',
			0.009: '#85D3FF',
			0.019: '#25B4FF',
			0.038: '#012DFF',
			0.057: '#7000D9',
			0.076: '#FF49C4',
			0.152: '#FFECF7',
			0.2285: '#36E2F4',
			0.381: '#007682',
			0.533: '#C38EF2',
			0.762: '#A34AF2',
			0.952: '#7100FF',
			1: '#7100FF',
		},
		dark: {
			0: 'rgba(96,96,96,0)',
			0.00003: 'rgba(96,96,96,0)',
			0.0003: 'rgba(96,96,96,0.5)',
			0.003: '#576066',
			0.009: '#85D3FF',
			0.019: '#25B4FF',
			0.038: '#012DFF',
			0.057: '#7000D9',
			0.076: '#FF49C4',
			0.152: '#FFECF7',
			0.2285: '#36E2F4',
			0.381: '#007682',
			0.533: '#C38EF2',
			0.762: '#A34AF2',
			0.952: '#7100FF',
			1: '#7100FF',
		},
	},
	'pressure-msl': {
		light: {
			0: '#30353D',
			0.2: '#8070EE',
			0.3: '#6B52E0',
			0.4: '#8070EE',
			0.6: '#67CB82',
			0.8: '#1F9544',
			1: '#0D742E',
		},
		dark: {
			0: '#30353D',
			0.2: '#8070EE',
			0.3: '#6B52E0',
			0.4: '#8070EE',
			0.6: '#67CB82',
			0.8: '#1F9544',
			1: '#0D742E',
		},
	},
	'wind-particles': {
		light: {
			0: '#99CA26',
			0.13: '#99CA26',
			0.27: '#55BFE9',
			0.45: '#467FBE',
			0.59: '#7E3F9D',
			0.76: '#B85500',
			0.92: '#E03200',
			1: '#E03200',
		},
		dark: {
			0: '#8FCAA7',
			0.13: '#8FCAA7',
			0.27: '#56A3C1',
			0.45: '#467FBE',
			0.59: '#7E3F9D',
			0.76: '#B85500',
			0.92: '#E03200',
			1: '#E03200',
		},
	},
	'wind-gusts': {
		light: {
			0: 'rgba(233,250,255,0.15)',
			0.25: 'rgba(149,226,245,0.5)',
			0.5: '#D48AFF',
			0.66: '#FF8BC7',
			0.85: '#EC3E9E',
			1: '#E03200',
		},
		dark: {
			0: 'rgba(152,195,207,0.15)',
			0.25: 'rgba(87,157,174,0.5)',
			0.5: '#622387',
			0.66: '#862256',
			0.85: '#84094C',
			1: '#972B0C',
		},
	},
	visibility: {
		light: {
			0: 'rgba(79,87,99,0.7)',
			0.25: 'rgba(66,107,140,0.5)',
			0.5: 'rgba(94,178,245,0.3)',
			1: 'rgba(94,178,245,0)',
		},
		dark: {
			0: 'rgba(79,87,99,0.7)',
			0.25: 'rgba(66,107,140,0.5)',
			0.5: 'rgba(94,178,245,0.3)',
			1: 'rgba(94,178,245,0)',
		},
	},
	'air-quality-index': {
		light: {
			0: 'rgba(134,255,156,0.3)',
			0.1: 'rgba(197,250,166,0.75)',
			0.3: '#FFFDC2',
			0.45: '#FFDAA2',
			0.6: '#EC694C',
			0.8: '#A94C4C',
			1: '#A94C4C',
		},
		dark: {
			0: 'rgba(65,95,71,0.5)',
			0.1: 'rgba(82,110,65,0.75)',
			0.3: '#737130',
			0.45: '#7D5B27',
			0.6: '#7D2D1B',
			0.8: '#660D0D',
			1: '#660D0D',
		},
	},
	'lightning-density': {
		light: {
			0.01: '#deebf7',
			0.02: '#c6dbef',
			0.04: '#9ecae1',
			0.08: '#6baed6',
			0.12: '#4292c6',
			0.16: '#2171b5',
			0.32: '#08519c',
			0.64: '#08306b',
			0.96: '#041c41',
		},
		dark: {
			0.01: '#041c41',
			0.02: '#08306b',
			0.04: '#08519c',
			0.08: '#2171b5',
			0.12: '#4292c6',
			0.16: '#6baed6',
			0.32: '#9ecae1',
			0.64: '#c6dbef',
			0.96: '#deebf7',
		},
	},
	'lightning-realtime': {
		light: {
			0: '#002973',
			0.167: '#3570F8',
			0.333: '#5A91FF',
			0.5: '#73B9FF',
			0.667: '#AAE4FF',
			0.833: '#D3F4FF',
		},
		dark: {
			0: '#D3F4FF',
			0.167: '#AAE4FF',
			0.333: '#73B9FF',
			0.5: '#5A91FF',
			0.667: '#3570F8',
			0.833: '#002973',
		},
	},
	'lightning-threatzone': {
		light: {
			0: '#EC973D',
			0.167: '#EFA95F',
			0.333: '#F3BB81',
			0.5: '#F6CDA2',
			0.667: '#FADFC4',
			0.833: '#FDF1E6',
			1: '#FDF1E6',
		},
		dark: {
			0: '#EC973D',
			0.167: '#D68631',
			0.333: '#C07525',
			0.5: '#AA6518',
			0.667: '#94540C',
			0.833: '#7E4300',
			1: '#7E4300',
		},
	},
	'hail-threats': {
		light: {
			0: '#DF0ED3',
			0.167: '#BE0DC9',
			0.333: '#9C0CBD',
			0.5: '#7B0BB2',
			0.667: '#660DB0',
			0.833: '#520BAE',
			1: '#520BAE',
		},
		dark: {
			0: '#DF0ED3',
			0.167: '#BE0DC9',
			0.333: '#9C0CBD',
			0.5: '#7B0BB2',
			0.667: '#660DB0',
			0.833: '#520BAE',
			1: '#520BAE',
		},
	},
	'power-lines-lightning-density': {
		0: '#a8a8a8',
		0.0003906: '#c9c95a',
		0.0019531: '#86cb94',
		0.003906: '#55bccb',
		0.007813: '#46b0cd',
		0.01563: '#3fa8c9',
		0.03125: '#50a1cd',
		0.04688: '#4b7dae',
		0.0625: '#596baa',
		0.09375: '#654f98',
		0.125: '#734076',
		0.25: '#864834',
		0.375: '#a55406',
		0.5: '#be4d0d',
		1: '#cf4218',
	},
	'satellite-infrared-color': {
		0: '#FFADAD',
		0.011: '#FF99A8',
		0.022: '#FF84A4',
		0.033: '#FF71A0',
		0.044: '#FE5E9C',
		0.055: '#FF4B98',
		0.066: '#F53C86',
		0.077: '#EB2D77',
		0.088: '#E11D66',
		0.099: '#D70F56',
		0.11: '#CE0046',
		0.121: '#BA0138',
		0.132: '#A90029',
		0.143: '#96011C',
		0.154: '#84000E',
		0.165: '#720100',
		0.176: '#840001',
		0.187: '#970000',
		0.198: '#AA0101',
		0.209: '#BD0000',
		0.22: '#D00101',
		0.231: '#D70900',
		0.242: '#DE1300',
		0.253: '#E41D01',
		0.264: '#EC2801',
		0.275: '#F33200',
		0.286: '#EE3F01',
		0.297: '#EA4C00',
		0.308: '#E85801',
		0.319: '#E36501',
		0.33: '#E17200',
		0.341: '#E58501',
		0.352: '#E89904',
		0.363: '#ECAD01',
		0.374: '#F0C102',
		0.385: '#F5D50A',
		0.396: '#C8CD06',
		0.407: '#9BC605',
		0.418: '#6EBE03',
		0.429: '#40B700',
		0.44: '#13AF01',
		0.451: '#118C2E',
		0.462: '#11685B',
		0.473: '#0E4689',
		0.484: '#0B23B8',
		0.495: '#0900E7',
		0.505: '#0C20EB',
		0.516: '#0F42EF',
		0.527: '#1362F3',
		0.538: '#1384F7',
		0.549: '#16A5FC',
		0.56: '#31A8FC',
		0.571: '#4CABFC',
		0.582: '#65AFFD',
		0.593: '#80B2FE',
		0.604: '#9BB6FE',
		0.615: '#898DFE',
		0.626: '#8079FF',
		0.637: '#7765FF',
		0.648: '#6E51FF',
		0.659: '#4C30CA',
		0.67: '#3C20AF',
		0.681: '#1B007B',
		0.692: '#684DA8',
		0.703: '#8E73BF',
		0.714: '#B59AD6',
		0.725: '#DCC1ED',
		0.736: '#E0B1F0',
		0.747: '#E6A1F4',
		0.758: '#EA91F7',
		0.769: '#EF81FB',
		0.78: '#F571FF',
		0.791: '#F45AF8',
		0.802: '#F143F1',
		0.813: '#F02DEA',
		0.824: '#ED15E3',
		0.835: '#DE00CE',
		0.846: '#DE00CE',
		0.857: '#D001C1',
		0.868: '#C100B2',
		0.879: '#B301A6',
		0.89: '#A50099',
		0.901: '#970092',
		0.912: '#8A018B',
		0.923: '#7D0184',
		0.934: '#70007D',
		0.945: '#630078',
		0.956: '#6B0088',
		0.967: '#740097',
		0.978: '#7D01A9',
		0.989: '#8501B9',
		1: '#8F00C9',
	},
} as const satisfies Partial<Record<LayerViewId | LayerMaskId, Record<ThemeMode, Record<number, string>> | Record<number, string>>>

export const legendRanges = {
	temperatures: {
		DEGREES_CELSIUS: [-20, 60],
		DEGREES_FAHRENHEIT: [-4, 140],
	},
	snow: {
		MILLIMETRES: [0, 0.08], // Actually in meters
		INCHES: [0, 3.15],
	},
	'pressure-msl': {
		HECTOPASCALS: [960, 1060],
		MILLIBARS: [960, 1060],
		INCH_OF_MERCURY: [28.35, 31.3],
	},
	'wind-particles': {
		METRES_PER_SECOND: [0, 36],
		MILES_PER_HOUR: [0, 80],
		KNOTS: [0, 70],
		KILOMETRES_PER_HOUR: [0, 132],
	},
	'wind-gusts': {
		METRES_PER_SECOND: [0, 60],
		MILES_PER_HOUR: [0, 134],
		KNOTS: [0, 117],
		KILOMETRES_PER_HOUR: [0, 215],
	},
	visibility: {
		KILOMETRES: [0, 16],
		METRES: [0, 16_000],
		MILES: [0, 10],
		NAUTICAL_MILES: [0, 8],
	},
	'air-quality-index': [0, 320],
	'lightning-density': {
		KILOMETRES: [0, 100],
		MILES: [0, 259],
		NAUTICAL_MILES: [0, 343],
	},
	'lightning-threatzone': [0, 60],
	'hail-threats': [0, 60],
	'power-lines-lightning-density': {
		KILOMETRES: [0, 256],
		MILES: [0, 663],
		NAUTICAL_MILES: [0, 878],
	},
	'satellite-infrared-color': {
		DEGREES_CELSIUS: [-125, 135],
		DEGREES_FAHRENHEIT: [-195, 275],
	},
	'radar-rain': [5, 85],
	'radar-mix': [5, 57],
	'radar-snow': [5, 57],
} as const satisfies Partial<Record<LayerViewId | LayerMaskId, Record<string, number[]> | number[]>>

export const legendLabels = {
	// Road weather summary needs dark colors
	'road-weather-summary': [
		{ id: 'bad', name: <I18n i18nKey={($) => $.legend.captions.road_weather_summary.bad} />, color: '#DA4B20' },
		{ id: 'fair', name: <I18n i18nKey={($) => $.legend.captions.road_weather_summary.fair} />, color: '#FDD02F' },
		{ id: 'good', name: <I18n i18nKey={($) => $.legend.captions.road_weather_summary.good} />, color: '#59B33D' },
	],
	fires: [
		{ id: 'new', name: <I18n i18nKey={($) => $.legend.captions.fires.new} />, color: '#E83A00' },
		{ id: 'active', name: <I18n i18nKey={($) => $.legend.captions.fires.active} />, color: '#E88900' },
		{ id: 'contained', name: <I18n i18nKey={($) => $.legend.captions.fires.contained} />, color: '#6C6461' },
	],
	'tropical-cyclones': [
		{
			id: 'low',
			name: <I18n i18nKey={($) => $.legend.captions['tropical-cyclones'].low} />,
			color: '#4875FB',
			element: <Icon name='Circle' sx={{ color: '#4875FB', fontSize: '12px', minWidth: '20px' }} />,
		},
		{
			id: 'depression',
			name: <I18n i18nKey={($) => $.legend.captions['tropical-cyclones'].depression} />,
			color: '#FFE700',
			element: <Icon name='Circle' sx={{ color: '#FFE700', fontSize: '12px', minWidth: '20px' }} />,
		},
		{
			id: 'storm',
			name: <I18n i18nKey={($) => $.legend.captions['tropical-cyclones'].storm} />,
			color: '#FEA800',
			element: <Icon name='Circle' sx={{ color: '#FEA800', fontSize: '12px', minWidth: '20px' }} />,
		},
		{
			id: 'cat1',
			name: <I18n i18nKey={($) => $.legend.captions['tropical-cyclones'].cat1} />,
			color: '#DD0002',
			element: <Icon name='Circle' sx={{ color: '#DD0002', fontSize: '12px', minWidth: '20px' }} />,
		},
		{
			id: 'cat2',
			name: <I18n i18nKey={($) => $.legend.captions['tropical-cyclones'].cat2} />,
			color: '#FF0491',
			element: <Icon name='Circle' sx={{ color: '#FF0491', fontSize: '12px', minWidth: '20px' }} />,
		},
		{
			id: 'cat3',
			name: <I18n i18nKey={($) => $.legend.captions['tropical-cyclones'].cat3} />,
			color: '#DD00FD',
			element: <Icon name='Circle' sx={{ color: '#DD00FD', fontSize: '12px', minWidth: '20px' }} />,
		},
		{
			id: 'cat4',
			name: <I18n i18nKey={($) => $.legend.captions['tropical-cyclones'].cat4} />,
			color: '#FEAFFF',
			element: <Icon name='Circle' sx={{ color: '#FEAFFF', fontSize: '12px', minWidth: '20px' }} />,
		},
		{
			id: 'cat5',
			name: <I18n i18nKey={($) => $.legend.captions['tropical-cyclones'].cat5} />,
			color: '#EFEFEF',
			element: <Icon name='Circle' sx={{ color: '#EFEFEF', fontSize: '12px', minWidth: '20px' }} />,
		},
		{
			id: 'typhoon',
			name: <I18n i18nKey={($) => $.legend.captions['tropical-cyclones'].typhoon} />,
			color: '#DD0002',
			element: <Icon name='Circle' sx={{ color: '#DD0002', fontSize: '12px', minWidth: '20px' }} />,
		},
		{
			id: 'superTyphon',
			name: <I18n i18nKey={($) => $.legend.captions['tropical-cyclones'].superTyphoon} />,
			color: '#EFEFEF',
			element: <Icon name='Circle' sx={{ color: '#EFEFEF', fontSize: '12px', minWidth: '20px' }} />,
		},
	],
	'legend.captions.lightning.eventTypeTitle': {
		light: [
			{
				id: 'cloudToGround',
				name: <I18n i18nKey={($) => $.legend.captions.lightning.cloudToGround} />,
				element: <Icon name='Circle' sx={{ color: colorscales['lightning-realtime'].light[0], fontSize: '12px', minWidth: '20px' }} />,
			},
			{
				id: 'cloudToCloud',
				name: <I18n i18nKey={($) => $.legend.captions.lightning.cloudToCloud} />,
				element: <Icon name='Circle' sx={{ color: colorscales['lightning-realtime'].light[0], fontSize: '6px', minWidth: '20px' }} />,
			},
		],
		dark: [
			{
				id: 'cloudToGround',
				name: <I18n i18nKey={($) => $.legend.captions.lightning.cloudToGround} />,
				element: <Icon name='Circle' sx={{ color: colorscales['lightning-realtime'].dark[0], fontSize: '12px', minWidth: '20px' }} />,
			},
			{
				id: 'cloudToCloud',
				name: <I18n i18nKey={($) => $.legend.captions.lightning.cloudToCloud} />,
				element: <Icon name='Circle' sx={{ color: colorscales['lightning-realtime'].dark[0], fontSize: '6px', minWidth: '20px' }} />,
			},
		],
	},
	'legend.captions.lightning.cloudToGround': {
		light: [
			{
				id: 'positive',
				name: <I18n i18nKey={($) => $.legend.captions.lightning.positiveCloudToGround} />,
				element: (
					<Icon name='LightningPlus' sx={{ color: colorscales['lightning-realtime'].light[0], fontSize: '20px', minWidth: '20px' }} />
				),
			},
			{
				id: 'negative',
				name: <I18n i18nKey={($) => $.legend.captions.lightning.negativeCloudToGround} />,
				element: (
					<Icon name='LightningMinus' sx={{ color: colorscales['lightning-realtime'].light[0], fontSize: '20px', minWidth: '20px' }} />
				),
			},
		],
		dark: [
			{
				id: 'positive',
				name: <I18n i18nKey={($) => $.legend.captions.lightning.positiveCloudToGround} />,
				element: (
					<Icon name='LightningPlus' sx={{ color: colorscales['lightning-realtime'].dark[0], fontSize: '20px', minWidth: '20px' }} />
				),
			},
			{
				id: 'negative',
				name: <I18n i18nKey={($) => $.legend.captions.lightning.negativeCloudToGround} />,
				element: (
					<Icon name='LightningMinus' sx={{ color: colorscales['lightning-realtime'].dark[0], fontSize: '20px', minWidth: '20px' }} />
				),
			},
		],
	},
	'lightning-alert-rings': [
		{ id: 'clear', name: <I18n i18nKey={($) => $.locations.rings.clear} />, element: <RingPreview variant='clear' mr={1} /> },
		{ id: 'info', name: <I18n i18nKey={($) => $.locations.rings.info} />, element: <RingPreview variant='info' mr={1} /> },
		{ id: 'warning', name: <I18n i18nKey={($) => $.locations.rings.warning} />, element: <RingPreview variant='warning' mr={1} /> },
		{ id: 'alarm', name: <I18n i18nKey={($) => $.locations.rings.alarm} />, element: <RingPreview variant='alarm' mr={1} /> },
	],
} as const satisfies Partial<Record<LayerViewId | ParseKeys, Label[] | Record<ThemeMode, Label[]>>>

export type Label = { id: string; name: ReactNode; color?: string; element?: ReactNode }
